import { applyDynamicFeatureFlags } from "@common/feature-flags";
import { getServerUrl } from "@/lib/server";
import { notifyBackendFlagsChanged } from "@/lib/runtime-config";
import { getShopFromLocalStorage } from "@/hooks/use-shop";

// Backend feature-flag overrides, keyed by dotted flag name (e.g.
// "missingProfileDataModal", "admin.shopGroups"). Persisted so the kiosk keeps the
// last-known flags when it has no connection to the backend.
const CACHE_KEY = "dynamic-feature-flags";

type FlagMap = Record<string, unknown>;

/**
 * Apply the last-known backend feature flags from localStorage. Call at boot
 * BEFORE rendering so the kiosk keeps the most recent flags even with no
 * internet connection. No-op when nothing is cached or the cache is corrupt.
 */
export function applyCachedDynamicFeatureFlags(): void {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) {
      return;
    }
    applyDynamicFeatureFlags(JSON.parse(raw) as FlagMap);
  } catch {
    // Missing/corrupt cache — fall back to the compiled defaults.
  }
}

/**
 * Fetch the dynamic feature flags from the backend for this origin, apply them,
 * and cache them for offline use. Throws on network/HTTP failure so the caller
 * (a react-query query on the start screen) can retry on the next refresh; the
 * previously cached flags stay in effect until a fetch succeeds again.
 */
export async function refreshDynamicFeatureFlags(
  signal?: AbortSignal,
): Promise<FlagMap> {
  const serverUrl = getServerUrl().replace(/\/$/, "");
  const origin = window.location.origin;
  // Send the kiosk key so the backend can apply per-shop flag overrides
  // (resolved via kiosk_shops.kiosk_key → shop_id).
  const kioskKey =
    getShopFromLocalStorage()?.kioskKey ??
    localStorage.getItem("kioskKeyBackup") ??
    undefined;
  const response = await fetch(
    `${serverUrl}/feature-flags?origin=${encodeURIComponent(origin)}`,
    { signal, headers: kioskKey ? { "kiosk-key": kioskKey } : undefined },
  );
  if (!response.ok) {
    throw new Error(`Failed to load feature flags: ${response.status}`);
  }

  const data = (await response.json()) as { flags: FlagMap };
  applyDynamicFeatureFlags(data.flags);
  notifyBackendFlagsChanged();
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(data.flags));
  } catch {
    // Storage unavailable/full — flags are still applied in memory.
  }
  return data.flags;
}
